/* NX PRO – next-level motion layer (vanilla, no dependencies) */
(() => {
  if (window.__nxPro) return; window.__nxPro = true;
  const $$ = (s, c = document) => [...c.querySelectorAll(s)];
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fine = matchMedia('(hover: hover) and (pointer: fine)').matches;
  const lowEnd = (navigator.deviceMemory && navigator.deviceMemory < 4) || (navigator.hardwareConcurrency && navigator.hardwareConcurrency < 4);
  const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));

  /* ---------- shared scroll loop ---------- */
  const subs = new Set();
  let lastY = scrollY, vel = 0;
  const loop = () => {
    const y = scrollY; vel += ((y - lastY) - vel) * .2; lastY = y;
    subs.forEach(fn => fn(y, vel));
    requestAnimationFrame(loop);
  };
  requestAnimationFrame(loop);
  const progressOf = (el) => { const r = el.getBoundingClientRect(); const total = el.offsetHeight - innerHeight; return clamp(-r.top / (total || 1)); };
  const visible = (el) => { const r = el.getBoundingClientRect(); return r.bottom > -100 && r.top < innerHeight + 100; };

  /* ---------- tiny WebGL helper ---------- */
  const glProgram = (gl, fs) => {
    const vs = 'attribute vec2 p;varying vec2 v;void main(){v=p*.5+.5;gl_Position=vec4(p,0.,1.);}';
    const sh = (t, s) => { const o = gl.createShader(t); gl.shaderSource(o, s); gl.compileShader(o); return o; };
    const pr = gl.createProgram(); gl.attachShader(pr, sh(gl.VERTEX_SHADER, vs)); gl.attachShader(pr, sh(gl.FRAGMENT_SHADER, fs)); gl.linkProgram(pr);
    if (!gl.getProgramParameter(pr, gl.LINK_STATUS)) return null;
    gl.useProgram(pr);
    gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const l = gl.getAttribLocation(pr, 'p'); gl.enableVertexAttribArray(l); gl.vertexAttribPointer(l, 2, gl.FLOAT, false, 0, 0);
    const u = {}; return { pr, u: (n) => u[n] || (u[n] = gl.getUniformLocation(pr, n)) };
  };
  const hex = (h, f) => { h = (h || f).trim().replace('#', ''); if (h.length === 3) h = h.split('').map(c => c + c).join(''); const n = parseInt(h, 16); return [(n >> 16 & 255) / 255, (n >> 8 & 255) / 255, (n & 255) / 255]; };
  const NOISE = 'float h(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);}float n(vec2 p){vec2 i=floor(p),f=fract(p);f=f*f*(3.-2.*f);return mix(mix(h(i),h(i+vec2(1,0)),f.x),mix(h(i+vec2(0,1)),h(i+vec2(1,1)),f.x),f.y);}float fbm(vec2 p){float s=0.,a=.5;for(int i=0;i<5;i++){s+=a*n(p);p*=2.02;a*=.5;}return s;}';

  /* ---------- Cinematic hero ---------- */
  const initCine = (sec) => {
    if (sec.dataset.nxInit) return; sec.dataset.nxInit = 1;
    const lines = sec.querySelector('[data-nx-lines]');
    if (lines && !lines.dataset.done) { lines.dataset.done = 1; lines.innerHTML = lines.innerHTML.split(/<br\s*\/?>|\n/).filter(s => s.trim()).map((l, i) => `<span class="nx-line" style="--li:${i}"><span>${l.trim()}</span></span>`).join(''); }
    setTimeout(() => sec.classList.add('is-in'), 80);
    const counter = sec.querySelector('[data-nx-count]');
    let draw = null;
    const canvas = sec.querySelector('[data-nx-cine-gl]');
    if (canvas && !reduce && !lowEnd) {
      const gl = canvas.getContext('webgl', { premultipliedAlpha: false, alpha: true });
      const prog = gl && glProgram(gl, 'precision mediump float;varying vec2 v;uniform vec2 r;uniform float t;uniform float p;uniform vec3 a;uniform vec3 b;' + NOISE +
        'void main(){vec2 uv=v;vec2 q=uv*3.;q.x*=r.x/r.y;float tt=t*.12;' +
        'for(int i=1;i<6;i++){float fi=float(i);q.x+=.3/fi*sin(fi*q.y+tt+.3*fi)+.6;q.y+=.3/fi*cos(fi*q.x+tt+.5*fi)-.4;}' +
        'float s=.5+.5*sin(q.x+q.y);float fold=pow(.5+.5*cos(q.x*1.4-q.y*.6+tt*2.),3.);vec3 col=mix(a,b,s)+fold*.4*vec3(1.,.82,.86);' +
        'float m=fbm(uv*vec2(r.x/r.y,1.)*2.2+vec2(0.,t*.03));float th=p*1.5-.2;float edge=smoothstep(th-.02,th+.12,m);' +
        'float rim=smoothstep(th-.06,th,m)-smoothstep(th,th+.06,m);col+=rim*vec3(1.,.9,.95)*1.2;' +
        'gl_FragColor=vec4(col,edge);}');
      if (prog) {
        gl.enable(gl.BLEND); gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
        const cs = getComputedStyle(sec);
        gl.uniform3fv(prog.u('a'), hex(cs.getPropertyValue('--silk-a'), '#3a0a1c'));
        gl.uniform3fv(prog.u('b'), hex(cs.getPropertyValue('--silk-b'), '#a3164a'));
        const resize = () => { const d = Math.min(devicePixelRatio || 1, 1.5) * .5; canvas.width = canvas.clientWidth * d; canvas.height = canvas.clientHeight * d; gl.viewport(0, 0, canvas.width, canvas.height); gl.uniform2f(prog.u('r'), canvas.width, canvas.height); };
        resize(); addEventListener('resize', resize);
        const t0 = performance.now();
        draw = (p) => { gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT); gl.uniform1f(prog.u('t'), (performance.now() - t0) / 1000); gl.uniform1f(prog.u('p'), p); gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4); };
      }
    }
    const start = parseFloat(sec.dataset.revealStart || '0.12');
    subs.add(() => {
      if (!visible(sec)) return;
      const p = progressOf(sec);
      sec.style.setProperty('--p', p.toFixed(4));
      if (counter) counter.textContent = String(Math.round(p * 100)).padStart(2, '0') + ' / 100';
      if (draw) draw(clamp(start + p * 1.1));
    });
  };

  /* ---------- Velocity marquee ---------- */
  const initVM = (sec) => {
    if (sec.dataset.nxInit) return; sec.dataset.nxInit = 1;
    const track = sec.querySelector('[data-nx-vm]'); if (!track) return;
    const items = $$('.nx-vm__item', track);
    let x = 0; const base = reduce ? 0 : parseFloat(sec.dataset.speed || '0.6');
    subs.add((y, v) => {
      if (!visible(sec)) return;
      const half = track.scrollWidth / 2 || 1;
      x -= base + Math.abs(v) * .25 * (reduce ? 0 : 1);
      if (x <= -half) x += half;
      track.style.transform = `translate3d(${x}px,0,0)`;
      const sk = clamp(v * -.35, -12, 12);
      items.forEach(i => i.style.setProperty('--skew', sk.toFixed(2) + 'deg'));
    });
  };

  /* ---------- Horizontal lookbook ---------- */
  const initLB = (sec) => {
    if (sec.dataset.nxInit) return; sec.dataset.nxInit = 1;
    const rail = sec.querySelector('[data-nx-rail]'); const panels = $$('.nx-lb__panel', sec); const count = sec.querySelector('[data-nx-lbcount]');
    if (!rail || reduce) return;
    const setH = () => { const extra = rail.scrollWidth - innerWidth; sec.style.setProperty('--lb-h', (innerHeight + Math.max(extra, 0)) + 'px'); };
    setH(); addEventListener('resize', setH);
    subs.add(() => {
      if (!visible(sec)) return;
      const p = progressOf(sec); const extra = Math.max(rail.scrollWidth - innerWidth, 0);
      rail.style.setProperty('--lbx', (-extra * p).toFixed(1) + 'px');
      const cx = innerWidth / 2; let active = 0, best = 1e9;
      panels.forEach((pn, i) => {
        const r = pn.getBoundingClientRect(); const d = (r.left + r.width / 2 - cx) / innerWidth;
        pn.style.setProperty('--ry', (d * -18).toFixed(2) + 'deg'); pn.style.setProperty('--sc', (1 - Math.min(Math.abs(d) * .12, .12)).toFixed(3));
        pn.style.setProperty('--px', (d * -60).toFixed(1) + 'px');
        if (Math.abs(d) < best) { best = Math.abs(d); active = i; }
      });
      if (count) count.textContent = String(active + 1).padStart(2, '0') + ' / ' + String(panels.length).padStart(2, '0');
    });
    $$('.nx-hot__dot', sec).forEach(b => b.addEventListener('click', e => { e.preventDefault(); const h = b.closest('.nx-hot'); $$('.nx-hot.is-open', sec).forEach(o => o !== h && o.classList.remove('is-open')); h.classList.toggle('is-open'); }));
  };

  /* ---------- Custom cursor ---------- */
  const initCursor = () => {
    if (!fine || reduce || document.documentElement.dataset.nxCursor !== 'true' || document.querySelector('.nx-cursor')) return;
    const c = document.createElement('div'); c.className = 'nx-cursor'; c.innerHTML = '<span></span>'; document.body.appendChild(c);
    const label = c.firstChild; let x = innerWidth / 2, y = innerHeight / 2, tx = x, ty = y;
    addEventListener('pointermove', e => { tx = e.clientX; ty = e.clientY; c.classList.remove('is-hidden'); }, { passive: true });
    document.addEventListener('pointerleave', () => c.classList.add('is-hidden'));
    document.addEventListener('pointerover', e => {
      const t = e.target.closest('[data-cursor], .card-product, .nx-lb__panel, a, button');
      if (t && (t.matches('[data-cursor]') || t.matches('.card-product') || t.closest('.card-product'))) { c.classList.add('is-big'); label.textContent = t.dataset.cursor || 'Zobacz'; }
      else c.classList.remove('is-big');
    });
    const tick = () => { x += (tx - x) * .2; y += (ty - y) * .2; c.style.transform = `translate3d(${x}px,${y}px,0)`; requestAnimationFrame(tick); };
    tick();
  };

  /* ---------- WebGL hover distortion on product cards ---------- */
  const initHoverGL = () => {
    if (!fine || reduce || lowEnd || document.documentElement.dataset.nxHoverGl !== 'true') return;
    const canvas = document.createElement('canvas'); canvas.className = 'nx-hovergl'; document.body.appendChild(canvas);
    const gl = canvas.getContext('webgl', { premultipliedAlpha: false, alpha: true }); if (!gl) return;
    const prog = glProgram(gl, 'precision mediump float;varying vec2 v;uniform sampler2D t0;uniform sampler2D t1;uniform float pr;uniform float tm;uniform vec2 m;uniform vec2 r;uniform vec2 s0;uniform vec2 s1;' + NOISE +
      'vec2 cover(vec2 uv,vec2 s){float ra=r.x/r.y,ri=s.x/s.y;vec2 k=ra>ri?vec2(1.,ri/ra):vec2(ra/ri,1.);return (uv-.5)*k+.5;}' +
      'void main(){vec2 uv=vec2(v.x,1.-v.y);float d=distance(uv,m);float rip=sin(d*28.-tm*5.)*.012*smoothstep(.45,0.,d)*sin(pr*3.1416+.4);' +
      'float nz=fbm(uv*3.+tm*.15);float a=smoothstep(pr*1.2-.2,pr*1.2,nz);' +
      'vec2 o=vec2(rip)+(nz-.5)*.06*sin(pr*3.1416);vec4 c0=texture2D(t0,cover(uv+o*(1.-pr),s0));vec4 c1=texture2D(t1,cover(uv-o*pr,s1));' +
      'gl_FragColor=mix(c1,c0,a);}');
    if (!prog) return;
    const tex = (i) => { const t = gl.createTexture(); gl.activeTexture(gl.TEXTURE0 + i); gl.bindTexture(gl.TEXTURE_2D, t); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE); return t; };
    const T0 = tex(0), T1 = tex(1); gl.uniform1i(prog.u('t0'), 0); gl.uniform1i(prog.u('t1'), 1);
    const cache = new Map();
    const load = (src) => cache.get(src) || (cache.set(src, new Promise((res, rej) => { const im = new Image(); im.crossOrigin = 'anonymous'; im.onload = () => res(im); im.onerror = rej; im.src = src; })), cache.get(src));
    const upload = (t, i, im) => { gl.activeTexture(gl.TEXTURE0 + i); gl.bindTexture(gl.TEXTURE_2D, t); gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, im); };
    let target = null, pr = 0, goal = 0, mx = .5, my = .5, raf = 0, token = 0; const t0 = performance.now();
    const frame = () => {
      raf = 0; if (!target) return;
      pr += (goal - pr) * .08;
      const r = target.getBoundingClientRect(); const dpr = Math.min(devicePixelRatio || 1, 2);
      canvas.style.cssText = `width:${r.width}px;height:${r.height}px;transform:translate3d(${r.left}px,${r.top}px,0);border-radius:${getComputedStyle(target).borderRadius}`;
      if (canvas.width !== Math.round(r.width * dpr)) { canvas.width = Math.round(r.width * dpr); canvas.height = Math.round(r.height * dpr); }
      gl.viewport(0, 0, canvas.width, canvas.height); gl.uniform2f(prog.u('r'), r.width, r.height);
      gl.uniform1f(prog.u('pr'), pr); gl.uniform1f(prog.u('tm'), (performance.now() - t0) / 1000); gl.uniform2f(prog.u('m'), mx, my);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      if (goal === 0 && pr < .01) { canvas.classList.remove('is-on'); target = null; return; }
      raf = requestAnimationFrame(frame);
    };
    const enter = async (media) => {
      const imgs = $$('img', media).filter(i => i.currentSrc || i.src).slice(0, 2);
      if (imgs.length < 2) return;
      const tk = ++token;
      try {
        const [a, b] = await Promise.all(imgs.map(i => load(i.currentSrc || i.src)));
        if (tk !== token) return;
        upload(T0, 0, a); upload(T1, 1, b);
        gl.uniform2f(prog.u('s0'), a.naturalWidth, a.naturalHeight); gl.uniform2f(prog.u('s1'), b.naturalWidth, b.naturalHeight);
        target = media; goal = 1; canvas.classList.add('is-on'); if (!raf) raf = requestAnimationFrame(frame);
      } catch (e) { /* CORS or load error: keep native hover */ }
    };
    document.addEventListener('pointerover', e => {
      const card = e.target.closest('.card-product'); if (!card) return;
      const sp = card.querySelector('.x-splide'); const media = sp && sp.parentElement;
      if (!media || media === target) return;
      if (!media.dataset.nxHover) {
        media.dataset.nxHover = 1;
        media.addEventListener('pointermove', ev => { const r = media.getBoundingClientRect(); mx = (ev.clientX - r.left) / r.width; my = (ev.clientY - r.top) / r.height; });
        media.addEventListener('pointerleave', () => { if (target === media) { goal = 0; token++; if (!raf) raf = requestAnimationFrame(frame); } });
      }
      enter(media);
    });
  };

  const init = (root = document) => {
    $$('[data-nx-cine]', root).forEach(initCine);
    $$('[data-nx-vm-sec]', root).forEach(initVM);
    $$('[data-nx-lb]', root).forEach(initLB);
  };
  const boot = () => { init(); initCursor(); initHoverGL(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', e => init(e.target));
})();
